import React, { useEffect, useState, useMemo } from "react";
import {
  X, GraduationCap, Users2, ClipboardList, Library, FileCheck2,
  Youtube, FileText, Link as LinkIcon, BookOpen, Calendar, ChevronRight,
} from "lucide-react";
import { api } from "../../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Slide-in drawer with detailed analytics for a single teacher. Opens from
 * the Admin → Overview tab when an admin clicks any teacher row.
 *
 * Sections:
 *   - Header strip (avatar, name, email, last login, devices, status)
 *   - 4-card summary KPI strip
 *   - 30-day activity sparkline (resources / assignments / submissions)
 *   - Groups list with expandable per-student roster
 *   - Assignments list with grading progress bars
 *   - Resource breakdown (by type + scope) + 10 most recent resources
 */
export default function TeacherAnalyticsDrawer({ teacherId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/teachers/${teacherId}/analytics`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || "Failed to load"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [teacherId]);

  return (
    <div className="fixed inset-0 z-[80] flex" data-testid="teacher-analytics-drawer">
      <button
        aria-label="Close"
        onClick={onClose}
        className="flex-1 bg-black/60 backdrop-blur-sm"
        data-testid="teacher-analytics-backdrop"
      />
      <aside className="w-full max-w-3xl bg-bone border-s border-border h-full overflow-y-auto shadow-2xl">
        <header className="sticky top-0 z-10 bg-white border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-sage/10 text-sage flex items-center justify-center font-semibold shrink-0">
              {(data?.teacher?.name || data?.teacher?.email || "?").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Teacher analytics</div>
              <h2 className="font-display text-lg font-semibold tracking-tight truncate">
                {data?.teacher?.name || data?.teacher?.email || "—"}
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-lg hover:bg-secondary"
            aria-label="Close"
            data-testid="teacher-analytics-close"
          >
            <X className="w-4 h-4 mx-auto" />
          </button>
        </header>

        <div className="px-6 py-6 space-y-6">
          {loading && <div className="text-center text-sm text-muted-foreground py-12">Loading analytics…</div>}
          {err && <div className="text-terracotta text-sm">{err}</div>}
          {data && (
            <>
              <TeacherHeader teacher={data.teacher} />
              <SummaryStrip summary={data.summary} />
              <Timeline timeline={data.timeline} />
              <GroupsBlock groups={data.groups} />
              <AssignmentsBlock assignments={data.assignments} />
              <ResourcesBlock
                byType={data.resources_by_type}
                byScope={data.resources_by_scope}
                recent={data.recent_resources}
              />
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

// ---------- header strip ----------
function TeacherHeader({ teacher }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-5 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm" data-testid="teacher-header">
      <Field label="Email" value={teacher.email} mono />
      <Field label="Status" value={teacher.status} />
      <Field label="Joined" value={teacher.joined_at ? new Date(teacher.joined_at).toLocaleDateString() : "—"} />
      <Field label="Last login" value={teacher.last_login_at ? timeAgo(teacher.last_login_at) : "Never"} />
      <Field label="Devices" value={teacher.devices_bound || 0} />
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-0.5">{label}</div>
      <div className={`font-medium ${mono ? "font-mono text-[13px]" : ""}`}>{value || "—"}</div>
    </div>
  );
}

// ---------- summary KPIs ----------
function SummaryStrip({ summary }) {
  const cards = [
    { Icon: Users2, label: "Groups", value: summary.groups, accent: "bg-indigo-500", testid: "summary-groups" },
    { Icon: GraduationCap, label: "Students", value: summary.students, accent: "bg-terracotta", testid: "summary-students" },
    { Icon: Library, label: "Resources", value: summary.resources, accent: "bg-amber-500", testid: "summary-resources" },
    { Icon: ClipboardList, label: "Assignments", value: summary.assignments, accent: "bg-violet-500", testid: "summary-assignments" },
  ];
  return (
    <div className="grid sm:grid-cols-4 gap-3" data-testid="teacher-summary">
      {cards.map(({ Icon, label, value, accent, testid }) => (
        <div key={label} data-testid={testid} className="bg-white border border-border rounded-2xl p-4 flex items-center gap-3">
          <div className={`w-9 h-9 rounded-xl ${accent} text-white flex items-center justify-center shrink-0`}>
            <Icon className="w-4 h-4" strokeWidth={1.8} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">{label}</div>
            <div className="font-display text-2xl font-semibold tracking-tight">{value ?? 0}</div>
          </div>
        </div>
      ))}
      {summary.submissions_total > 0 && (
        <div className="sm:col-span-4 bg-white border border-border rounded-2xl p-4" data-testid="summary-submissions">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <FileCheck2 className="w-4 h-4 text-teal-600" />
              <span className="font-medium text-sm">Submissions grading progress</span>
            </div>
            <span className="text-xs text-muted-foreground">
              {summary.submissions_graded}/{summary.submissions_total} graded · {summary.submissions_pending} pending
            </span>
          </div>
          <ProgressBar value={summary.submissions_graded} total={summary.submissions_total} accent="bg-teal-500" />
        </div>
      )}
    </div>
  );
}

function ProgressBar({ value, total, accent = "bg-sage" }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="h-2 rounded-full bg-secondary overflow-hidden">
      <div className={`h-full ${accent}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

// ---------- 30-day timeline sparkline ----------
function Timeline({ timeline }) {
  const max = useMemo(() => {
    if (!timeline?.length) return 1;
    return Math.max(1, ...timeline.flatMap((d) => [d.resources, d.assignments, d.submissions]));
  }, [timeline]);
  if (!timeline?.length) return null;

  const W = 720;
  const H = 120;
  const stepX = W / (timeline.length - 1 || 1);
  const linePath = (key, color) => {
    const pts = timeline.map((d, i) => `${i * stepX},${H - (d[key] / max) * (H - 14) - 4}`).join(" ");
    return <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />;
  };

  return (
    <section data-testid="teacher-timeline">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-medium text-sm">Last 30 days activity</h3>
        <Legend />
      </div>
      <div className="bg-white border border-border rounded-2xl p-4 overflow-x-auto">
        <svg viewBox={`-2 -2 ${W + 4} ${H + 4}`} className="w-full h-auto min-w-[480px]" role="img" aria-label="30-day activity">
          {linePath("resources", "#c89a4b")}
          {linePath("assignments", "#8a7ca8")}
          {linePath("submissions", "#4f8f94")}
        </svg>
      </div>
    </section>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Resources</span>
      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-violet-500" /> Assignments</span>
      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-teal-500" /> Submissions</span>
    </div>
  );
}

// ---------- groups block ----------
function GroupsBlock({ groups }) {
  const [expanded, setExpanded] = useState(null);
  if (!groups?.length) {
    return (
      <section data-testid="teacher-groups-empty">
        <h3 className="font-medium text-sm mb-2">Groups</h3>
        <div className="bg-white border border-dashed border-border rounded-2xl p-6 text-center text-sm text-muted-foreground">
          This teacher hasn't created any groups yet.
        </div>
      </section>
    );
  }
  return (
    <section data-testid="teacher-groups">
      <h3 className="font-medium text-sm mb-2">Groups · {groups.length}</h3>
      <div className="bg-white border border-border rounded-2xl divide-y divide-border">
        {groups.map((g) => {
          const open = expanded === g.group_id;
          return (
            <div key={g.group_id} data-testid={`group-row-${g.group_id}`}>
              <button
                onClick={() => setExpanded(open ? null : g.group_id)}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-bone/40"
                data-testid={`group-toggle-${g.group_id}`}
              >
                <Users2 className="w-4 h-4 text-indigo-500 shrink-0" />
                <div className="flex-1 min-w-0 text-start">
                  <div className="font-medium text-sm truncate">{g.name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {g.student_count} students · {g.assignment_count} assignments
                    {g.submission_total > 0 && ` · ${g.submission_graded}/${g.submission_total} graded`}
                  </div>
                </div>
                <ChevronRight className={`w-4 h-4 transition-transform shrink-0 ${open ? "rotate-90" : ""}`} />
              </button>
              {open && g.students?.length > 0 && (
                <ul className="bg-bone/30 px-4 py-3 grid sm:grid-cols-2 gap-2" data-testid={`group-roster-${g.group_id}`}>
                  {g.students.map((s) => (
                    <li key={s.user_id} className="text-xs flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-sage" />
                      <span className="font-medium">{s.name || s.email}</span>
                      {s.last_login_at && (
                        <span className="text-muted-foreground ms-auto">{timeAgo(s.last_login_at)}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ---------- assignments block ----------
function AssignmentsBlock({ assignments }) {
  if (!assignments?.length) return null;
  return (
    <section data-testid="teacher-assignments">
      <h3 className="font-medium text-sm mb-2">Assignments · {assignments.length}</h3>
      <div className="bg-white border border-border rounded-2xl divide-y divide-border">
        {assignments.slice(0, 12).map((a) => {
          const sub = a.submissions || { total: 0, graded: 0, pending: 0 };
          return (
            <div key={a.assignment_id} className="px-4 py-3" data-testid={`assignment-row-${a.assignment_id}`}>
              <div className="flex items-start gap-3">
                <ClipboardList className="w-4 h-4 text-violet-500 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm leading-tight truncate">{a.title || "(untitled)"}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {a.kind} · created {a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}
                    {a.due_at && ` · due ${new Date(a.due_at).toLocaleDateString()}`}
                  </div>
                  {sub.total > 0 && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
                        <span>{sub.graded}/{sub.total} graded</span>
                        <span>{sub.pending} pending</span>
                      </div>
                      <ProgressBar value={sub.graded} total={sub.total} accent="bg-violet-500" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ---------- resources block ----------
const RES_TYPE_META = {
  youtube: { Icon: Youtube, label: "YouTube", color: "text-[#d8382b]" },
  file: { Icon: FileText, label: "Files", color: "text-terracotta" },
  link: { Icon: LinkIcon, label: "Links", color: "text-sage" },
};

function ResourcesBlock({ byType, byScope, recent }) {
  if (!recent?.length) return null;
  return (
    <section data-testid="teacher-resources-block">
      <h3 className="font-medium text-sm mb-2">Resources uploaded · {recent.length}+</h3>

      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <div className="bg-white border border-border rounded-2xl p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-2">By type</div>
          <div className="grid grid-cols-3 gap-2 text-center">
            {Object.entries(byType || {}).map(([k, v]) => {
              const meta = RES_TYPE_META[k] || RES_TYPE_META.link;
              const Icon = meta.Icon;
              return (
                <div key={k} className="bg-bone/40 rounded-lg p-2.5" data-testid={`res-type-${k}`}>
                  <Icon className={`w-4 h-4 mx-auto ${meta.color}`} />
                  <div className="font-display text-xl font-semibold mt-1">{v}</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-[0.15em]">{meta.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-border rounded-2xl p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-2">By scope</div>
          <div className="grid grid-cols-3 gap-2 text-center">
            {[["chapter", BookOpen, "text-sage"], ["group", Users2, "text-indigo-500"], ["assignment", ClipboardList, "text-violet-500"]].map(([k, Icon, color]) => (
              <div key={k} className="bg-bone/40 rounded-lg p-2.5" data-testid={`res-scope-${k}`}>
                <Icon className={`w-4 h-4 mx-auto ${color}`} />
                <div className="font-display text-xl font-semibold mt-1">{(byScope || {})[k] || 0}</div>
                <div className="text-[10px] text-muted-foreground uppercase tracking-[0.15em]">{k}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border border-border rounded-2xl divide-y divide-border">
        {recent.slice(0, 8).map((r) => {
          const meta = RES_TYPE_META[r.type] || RES_TYPE_META.link;
          const Icon = meta.Icon;
          const fileUrl = r.type === "file" && r.file_id ? `${BACKEND}/api/files/${r.file_id}` : null;
          const href = r.type === "youtube" ? r.youtube_url : (r.type === "file" ? fileUrl : r.external_url);
          return (
            <a
              key={r.resource_id}
              href={href || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-3 flex items-center gap-3 hover:bg-bone/40"
              data-testid={`recent-resource-${r.resource_id}`}
            >
              <Icon className={`w-4 h-4 ${meta.color} shrink-0`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium leading-tight truncate">{r.title || "(untitled)"}</div>
                <div className="text-[11px] text-muted-foreground flex items-center gap-2">
                  <Calendar className="w-3 h-3" />
                  {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                  <span className="opacity-50">·</span>
                  <span className="uppercase tracking-[0.12em] text-[10px]">{r.scope}</span>
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </section>
  );
}

// ---------- helpers ----------
function timeAgo(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const mins = Math.max(1, Math.floor((Date.now() - d.getTime()) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}
